import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tab, Tabs, Button, Form, Alert, Spinner, Modal } from 'react-bootstrap';
import { menuAPI, standingOrdersAPI, ordersAPI, orderSkipsAPI } from '../../services/api';
import { getCurrentDayOfWeek, getCurrentTimeMinutes, calculateNextOrderDate, parseCutoffTime, formatCutoffTime } from '../../utils/timezone';
import { MENU_CATEGORIES, EMPTY_MENU, CATEGORY_STYLES } from '../../utils/constants';

function CustomerPortal() {
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState(EMPTY_MENU);
  const [timeSlots, setTimeSlots] = useState([]);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [standingOrders, setStandingOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [editingStandingOrders, setEditingStandingOrders] = useState({}); // For grid-style editing
  
  const [selectedDay, setSelectedDay] = useState(null); // Will be set to next available
  const [selectedSlot, setSelectedSlot] = useState(null); // Will be set to next available
  const [currentOrder, setCurrentOrder] = useState({});
  const [notes, setNotes] = useState('');
  
  const [message, setMessage] = useState({ type: '', text: '' });
  const [orderMessage, setOrderMessage] = useState({ type: '', text: '' }); // Message near submit button
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState({ dayName: '', slotName: '', orderDate: '' });
  const [showStandingSuccessModal, setShowStandingSuccessModal] = useState(false);
  const [standingSuccessDetails, setStandingSuccessDetails] = useState({ dayName: '', slotName: '', effectiveDate: '', daysUntilEffective: 0 });
  const [existingSubmittedOrder, setExistingSubmittedOrder] = useState(null); // Track if order already submitted
  const [standingOrderQuantities, setStandingOrderQuantities] = useState({}); // Standing order for comparison
  const [submitting, setSubmitting] = useState(false); // Prevent double-clicks
  const [savingStandingOrders, setSavingStandingOrders] = useState(false); // Prevent double-clicks on standing orders
  const [orderSkips, setOrderSkips] = useState([]); // Active skips for this user
  const [skipping, setSkipping] = useState(false); // Prevent double-clicks on skip

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (Object.keys(menuItems).length > 0) {
      loadCurrentOrder();
    }
  }, [selectedDay, selectedSlot, standingOrders, orderHistory, menuItems]);

  // Reload menu items with lock status when day/slot changes
  useEffect(() => {
    if (selectedDay && selectedSlot) {
      loadMenuItemsWithLocks(selectedDay, selectedSlot);
    }
  }, [selectedDay, selectedSlot]);

  const loadMenuItemsWithLocks = async (dayId, slotId) => {
    try {
      const response = await menuAPI.getMenuItems(dayId, slotId);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to load menu items with locks:', error);
    }
  };

  // Populate editingStandingOrders when standingOrders changes
  useEffect(() => {
    if (standingOrders.length > 0) {
      const orders = {};
      standingOrders.forEach(so => {
        const key = `${so.day_of_week_id}-${so.time_slot_id}-${so.menu_item_id}`;
        orders[key] = so.quantity;
      });
      setEditingStandingOrders(orders);
    }
  }, [standingOrders]);

  const loadData = async () => {
    try {
      const [menuRes, slotsRes, daysRes, standingRes, historyRes, skipsRes] = await Promise.all([
        menuAPI.getMenuItems(),
        menuAPI.getTimeSlots(),
        menuAPI.getDaysOfWeek(),
        standingOrdersAPI.get(),
        ordersAPI.getHistory(),
        orderSkipsAPI.get()
      ]);

      setMenuItems(menuRes.data);
      const slots = slotsRes.data;
      const days = daysRes.data;
      setTimeSlots(slots);
      setDaysOfWeek(days);
      setStandingOrders(standingRes.data);
      setOrderHistory(historyRes.data);
      setOrderSkips(skipsRes.data);

      // Calculate and set the next available order date/slot
      const nextAvailable = calculateNextAvailableSlot(days, slots);
      if (nextAvailable) {
        setSelectedDay(nextAvailable.dayId);
        setSelectedSlot(nextAvailable.slotId);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'danger', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  // Refresh order history without changing selected day/slot
  const refreshOrderHistory = async () => {
    try {
      const [standingRes, historyRes] = await Promise.all([
        standingOrdersAPI.get(),
        ordersAPI.getHistory()
      ]);
      setStandingOrders(standingRes.data);
      setOrderHistory(historyRes.data);
      return historyRes.data; // Return the fresh data
    } catch (error) {
      console.error('Failed to refresh order history:', error);
      return null;
    }
  };

  // Load current order quantities for the selected day/slot.
  // Accepts optional fresh order history to avoid stale state after submissions.
  const loadCurrentOrder = (freshOrderHistory) => {
    const history = freshOrderHistory || orderHistory;
    if (!selectedDay || !selectedSlot || !history) return;
    
    // Build standing order quantities for comparison
    const standingQty = {};
    Object.values(menuItems).flat().forEach(item => {
      const standing = standingOrders.find(
        so => Number(so.day_of_week_id) === Number(selectedDay) && 
              Number(so.time_slot_id) === Number(selectedSlot) && 
              Number(so.menu_item_id) === Number(item.id)
      );
      standingQty[item.id] = standing?.quantity || 0;
    });
    setStandingOrderQuantities(standingQty);
    
    // Calculate the order date using timezone utility - matches backend exactly
    const orderDateStr = calculateNextOrderDate(selectedDay);
    
    // Find matching PENDING or SUBMITTED orders only (ignore replaced/cancelled)
    const matchingOrders = history.filter(order => 
      Number(order.day_of_week_id) === Number(selectedDay) && 
      Number(order.time_slot_id) === Number(selectedSlot) &&
      order.order_date.split('T')[0] === orderDateStr &&
      (order.status === 'pending' || order.status === 'submitted')
    );
    
    // Sort by submitted_at descending to ensure we get the newest
    matchingOrders.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
    
    const existingOrder = matchingOrders[0] || null;
    
    if (existingOrder) {
      setExistingSubmittedOrder(existingOrder);
      const order = {};
      Object.values(menuItems).flat().forEach(item => {
        const orderItem = existingOrder.items.find(i => Number(i.menu_item_id) === Number(item.id));
        order[item.id] = orderItem?.quantity || 0;
      });
      setCurrentOrder(order);
    } else {
      setExistingSubmittedOrder(null);
      setCurrentOrder({...standingQty});
    }
  };

  // Calculate next available order date/slot that hasn't passed cutoff
  const calculateNextAvailableSlot = (days, slots) => {
    const currentTime = getCurrentTimeMinutes();
    const currentJSDay = getCurrentDayOfWeek(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    
    // Convert JS day (0-6) to DB day (1-7 where 1=Monday, 7=Sunday)
    const currentDBDay = currentJSDay === 0 ? 7 : currentJSDay;

    // Sort time slots by display order
    const sortedSlots = [...slots].sort((a, b) => a.display_order - b.display_order);
    
    // Check today first
    for (const slot of sortedSlots) {
      if (!slot.active) continue;
      
      // Parse cutoff time (format: "HH:MM:SS")
      const cutoffMinutes = parseCutoffTime(slot.cutoff_time);
      
      // If cutoff hasn't passed yet today
      if (currentTime < cutoffMinutes) {
        return {
          dayId: currentDBDay,
          slotId: slot.id
        };
      }
    }

    // If all slots today are closed, find next day with earliest slot
    // Start from tomorrow and look up to 7 days ahead
    for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
      const futureJSDay = (currentJSDay + daysAhead) % 7;
      const futureDBDay = futureJSDay === 0 ? 7 : futureJSDay;
      
      // Return the first active slot for this day
      const firstSlot = sortedSlots.find(slot => slot.active);
      if (firstSlot) {
        return {
          dayId: futureDBDay,
          slotId: firstSlot.id
        };
      }
    }

    // Fallback to Monday morning if nothing found (shouldn't happen)
    return {
      dayId: 1,
      slotId: sortedSlots[0]?.id || 1
    };
  };

  const updateQuantity = (itemId, quantity) => {
    setCurrentOrder(prev => ({
      ...prev,
      [itemId]: Math.max(0, quantity)
    }));
  };

  // Check if a PENDING order already exists for selected day/slot
  const hasExistingOrder = () => {
    if (!orderHistory || !selectedDay || !selectedSlot) return false;
    
    // Calculate the order date using timezone utility
    const orderDateStr = calculateNextOrderDate(selectedDay);
    
    return orderHistory.some(order => 
      order.day_of_week_id === selectedDay && 
      order.time_slot_id === selectedSlot &&
      order.order_date.split('T')[0] === orderDateStr &&
      (order.status === 'pending' || order.status === 'submitted')
    );
  };

  // Check if cutoff has passed for the selected day/slot
  const isCutoffPassed = () => {
    if (!selectedDay || !selectedSlot) return false;
    
    const selectedSlotData = timeSlots.find(s => s.id === selectedSlot);
    if (!selectedSlotData) return false;
    
    const currentTime = getCurrentTimeMinutes();
    const currentJSDay = getCurrentDayOfWeek();
    const currentDBDay = currentJSDay === 0 ? 7 : currentJSDay;
    
    const cutoffMinutes = parseCutoffTime(selectedSlotData.cutoff_time);
    const isToday = currentDBDay === selectedDay;
    
    return isToday && currentTime >= cutoffMinutes;
  };

  // Determine if the order form should be read-only
  // Only read-only if cutoff has passed (not just because an order exists)
  const isOrderFormReadOnly = isCutoffPassed();

  // Check if the current day/slot/date has a skip
  const getCurrentSkip = () => {
    if (!selectedDay || !selectedSlot) return null;
    const orderDateStr = calculateNextOrderDate(selectedDay);
    return orderSkips.find(s => 
      Number(s.day_of_week_id) === Number(selectedDay) &&
      Number(s.time_slot_id) === Number(selectedSlot) &&
      (s.skip_date?.split('T')[0] || s.skip_date) === orderDateStr
    ) || null;
  };

  const currentSkip = getCurrentSkip();

  // Check if user has any standing order items for this day/slot
  const hasStandingOrderForSlot = () => {
    if (!selectedDay || !selectedSlot) return false;
    return standingOrders.some(so =>
      Number(so.day_of_week_id) === Number(selectedDay) &&
      Number(so.time_slot_id) === Number(selectedSlot) &&
      so.quantity > 0
    );
  };

  const handleSkipOrder = async () => {
    if (skipping) return;
    setSkipping(true);
    try {
      const orderDateStr = calculateNextOrderDate(selectedDay);

      // If there's an existing pending order, cancel it first
      if (existingSubmittedOrder && existingSubmittedOrder.status === 'pending') {
        await ordersAPI.cancel(existingSubmittedOrder.id);
      }

      await orderSkipsAPI.create({
        dayOfWeekId: selectedDay,
        timeSlotId: selectedSlot,
        skipDate: orderDateStr
      });
      const skipsRes = await orderSkipsAPI.get();
      setOrderSkips(skipsRes.data);
      const dayName = daysOfWeek.find(d => d.id === selectedDay)?.day_name || '';
      const slotName = timeSlots.find(s => s.id === selectedSlot)?.slot_name || '';
      setMessage({ type: 'success', text: `Skipped ${dayName} ${slotName} (${orderDateStr}). No order will be auto-submitted.` });
      // Refresh to clear the existing order display
      const freshHistory = await refreshOrderHistory();
      if (freshHistory) loadCurrentOrder(freshHistory);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to skip order';
      setMessage({ type: 'danger', text: errorMsg });
    } finally {
      setSkipping(false);
    }
  };

  const handleUnskipOrder = async () => {
    if (skipping || !currentSkip) return;
    setSkipping(true);
    try {
      await orderSkipsAPI.remove(currentSkip.id);
      const skipsRes = await orderSkipsAPI.get();
      setOrderSkips(skipsRes.data);
      setMessage({ type: 'success', text: 'Skip removed. Your standing order will be submitted as normal.' });
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to remove skip' });
    } finally {
      setSkipping(false);
    }
  };

  const handleSubmitOrder = async (forceSubmit = false) => {
    if (submitting) return; // Prevent double-clicks
    
    try {
      // Clear any previous order message
      setOrderMessage({ type: '', text: '' });
      
      // Validate cutoff hasn't passed (using timezone utility)
      const selectedSlotData = timeSlots.find(s => s.id === selectedSlot);
      if (selectedSlotData) {
        const currentTime = getCurrentTimeMinutes();
        const currentJSDay = getCurrentDayOfWeek();
        const currentDBDay = currentJSDay === 0 ? 7 : currentJSDay;
        
        const cutoffMinutes = parseCutoffTime(selectedSlotData.cutoff_time);
        const isToday = currentDBDay === selectedDay;
        
        if (isToday && currentTime >= cutoffMinutes) {
          setOrderMessage({ 
            type: 'danger', 
            text: `Cannot submit order. Cutoff time (${formatCutoffTime(selectedSlotData.cutoff_time)}) has passed. Please select a different day or time slot.` 
          });
          return;
        }
      }

      const items = Object.entries(currentOrder)
        .filter(([_, qty]) => qty > 0)
        .map(([itemId, qty]) => ({
          menuItemId: parseInt(itemId),
          quantity: qty
        }));

      if (items.length === 0) {
        setOrderMessage({ type: 'warning', text: 'Please add at least one item' });
        return;
      }

      // Check for existing order (unless force submitting after confirmation)
      if (!forceSubmit && hasExistingOrder()) {
        setPendingOrderData({
          dayOfWeekId: selectedDay,
          timeSlotId: selectedSlot,
          items,
          notes
        });
        setShowDuplicateConfirm(true);
        return;
      }

      setSubmitting(true); // Disable button

      await ordersAPI.submit({
        dayOfWeekId: selectedDay,
        timeSlotId: selectedSlot,
        items,
        notes
      });

      // Get day and slot names for success message
      const dayName = daysOfWeek.find(d => d.id === selectedDay)?.day_name || '';
      const slotName = timeSlots.find(s => s.id === selectedSlot)?.slot_name || '';
      
      // Calculate order date for display using timezone utility
      const orderDateStr = calculateNextOrderDate(selectedDay);
      const orderDateFormatted = new Date(orderDateStr + 'T12:00:00').toLocaleDateString('en-US', { 
        timeZone: 'America/Chicago',
        weekday: 'long', 
        month: 'long', 
        day: 'numeric' 
      });
      
      // Show success modal
      setSuccessDetails({
        dayName,
        slotName,
        orderDate: orderDateFormatted
      });
      setShowSuccessModal(true);
      
      setNotes('');
      
      // Refresh order history and immediately load with fresh data
      const freshHistory = await refreshOrderHistory();
      if (freshHistory) {
        loadCurrentOrder(freshHistory);
      }
    } catch (error) {
      setOrderMessage({ type: 'danger', text: error.response?.data?.error || 'Failed to submit order' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDuplicateOrder = async () => {
    if (submitting) return; // Prevent double-clicks
    
    setShowDuplicateConfirm(false);
    if (pendingOrderData) {
      setSubmitting(true);
      try {
        await ordersAPI.submit(pendingOrderData);
        
        // Get day and slot names for success message
        const dayName = daysOfWeek.find(d => d.id === pendingOrderData.dayOfWeekId)?.day_name || '';
        const slotName = timeSlots.find(s => s.id === pendingOrderData.timeSlotId)?.slot_name || '';
        
        // Calculate order date for display using timezone utility
        const orderDateStr = calculateNextOrderDate(pendingOrderData.dayOfWeekId);
        const orderDateFormatted = new Date(orderDateStr + 'T12:00:00').toLocaleDateString('en-US', { 
          timeZone: 'America/Chicago',
          weekday: 'long', 
          month: 'long', 
          day: 'numeric' 
        });
        
        // Show success modal
        setSuccessDetails({
          dayName,
          slotName,
          orderDate: orderDateFormatted
        });
        setShowSuccessModal(true);
        
        setNotes('');
        setPendingOrderData(null);
        
        // Refresh order history and immediately load with fresh data
        const freshHistory = await refreshOrderHistory();
        if (freshHistory) {
          loadCurrentOrder(freshHistory);
        }
      } catch (error) {
        setOrderMessage({ type: 'danger', text: error.response?.data?.error || 'Failed to submit order' });
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Update quantity in the grid
  const updateStandingOrderQuantity = (dayId, slotId, itemId, quantity) => {
    const key = `${dayId}-${slotId}-${itemId}`;
    setEditingStandingOrders(prev => ({
      ...prev,
      [key]: Math.max(0, quantity)
    }));
  };

  // Save all standing orders at once
  const handleSaveAllStandingOrders = async () => {
    if (savingStandingOrders) return; // Prevent double-clicks
    
    setSavingStandingOrders(true);
    try {
      // Group orders by day/slot for bulk update
      const ordersByDaySlot = {};
      
      Object.entries(editingStandingOrders).forEach(([key, quantity]) => {
        const [dayId, slotId, itemId] = key.split('-').map(Number);
        const dsKey = `${dayId}-${slotId}`;
        
        if (!ordersByDaySlot[dsKey]) {
          ordersByDaySlot[dsKey] = { dayId, slotId, items: [] };
        }
        
        ordersByDaySlot[dsKey].items.push({ menuItemId: itemId, quantity });
      });

      // Save each day/slot combination
      let lastResponse = null;
      for (const { dayId, slotId, items } of Object.values(ordersByDaySlot)) {
        lastResponse = await standingOrdersAPI.bulkUpdate({
          dayOfWeekId: dayId,
          timeSlotId: slotId,
          items
        });
      }

      // Show success modal with info from last response
      if (lastResponse) {
        const effectiveDate = lastResponse.data.effectiveDate;
        const daysUntilEffective = lastResponse.data.daysUntilEffective;

        // Parse date parts directly to avoid timezone shift
        // effectiveDate is "YYYY-MM-DD" format
        const [year, month, day] = effectiveDate.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day); // Local date, no UTC shift
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });

        setStandingSuccessDetails({
          dayName: 'All Days',
          slotName: 'All Slots',
          effectiveDate: formattedDate,
          daysUntilEffective: daysUntilEffective || 0
        });
        setShowStandingSuccessModal(true);
      }

      loadData();
    } catch (error) {
      console.error('Standing orders save error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update standing orders';
      setMessage({ type: 'danger', text: errorMsg });
    } finally {
      setSavingStandingOrders(false);
    }
  };

  // Legacy single day/slot update (kept for compatibility)
  const handleUpdateStanding = async () => {
    try {
      const items = Object.entries(currentOrder).map(([itemId, qty]) => ({
        menuItemId: parseInt(itemId),
        quantity: qty
      }));

      const response = await standingOrdersAPI.bulkUpdate({
        dayOfWeekId: selectedDay,
        timeSlotId: selectedSlot,
        items
      });

      // Get day and slot names for the modal
      const dayName = daysOfWeek.find(d => d.id === selectedDay)?.day_name || '';
      const slotName = timeSlots.find(s => s.id === selectedSlot)?.slot_name || '';

      // Show when changes will become effective
      const effectiveDate = response.data.effectiveDate;
      const daysUntilEffective = response.data.daysUntilEffective;

      // Parse date parts directly to avoid timezone shift
      const [year, month, day] = effectiveDate.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // Local date, no UTC shift
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });

      // Show success modal
      setStandingSuccessDetails({
        dayName,
        slotName,
        effectiveDate: formattedDate,
        daysUntilEffective: daysUntilEffective || 0
      });
      setShowStandingSuccessModal(true);

      loadData();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to update standing order' });
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4 mb-5">
      {message.text && (
        <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
          {message.text}
        </Alert>
      )}

      <Tabs defaultActiveKey="order" className="mb-4">
        <Tab eventKey="order" title="Adjust Your Next Order">
          <Card className="fade-in">
            <Card.Header>📝 Adjust Your Next Order</Card.Header>
            <Card.Body>
              {existingSubmittedOrder ? (
                <Alert variant={isOrderFormReadOnly ? "secondary" : (existingSubmittedOrder.status === 'submitted' ? "success" : "info")} className="mb-4">
                  {isOrderFormReadOnly ? (
                    <>
                      <strong>⏰ Cutoff Passed - Order Locked</strong>
                      <br />
                      Your order has been submitted and the cutoff time has passed. No further changes can be made.
                    </>
                  ) : existingSubmittedOrder.status === 'submitted' ? (
                    <>
                      <strong>✅ Order Submitted</strong>
                      <br />
                      Your order has been finalized. The cutoff time has passed.
                    </>
                  ) : (
                    <>
                      <strong>📝 Order Pending - You Can Still Revise</strong>
                      <br />
                      Your order is pending until the cutoff time. You can revise it until then.
                      {existingSubmittedOrder.order_type === 'modified' && (
                        <><br /><span className="text-warning">⚡ This order differs from your standing order.</span></>
                      )}
                      {existingSubmittedOrder.order_type === 'standing' && (
                        <><br /><span className="text-muted">🔄 This was auto-submitted from your standing order.</span></>
                      )}
                    </>
                  )}
                </Alert>
              ) : (
                <Alert variant="info" className="mb-4">
                  <strong>One-Time Order:</strong> Changes here apply only to your next order. 
                  To update recurring weekly orders, use the <strong>Standing Orders</strong> tab.
                </Alert>
              )}
              {currentSkip && (
                <Alert variant="warning" className="mb-4 d-flex justify-content-between align-items-center">
                  <div>
                    <strong>⏭️ This order is skipped</strong>
                    <br />
                    No order will be auto-submitted for this date. Your standing order is unchanged for future weeks.
                  </div>
                  <Button 
                    variant="outline-warning" 
                    size="sm" 
                    onClick={handleUnskipOrder}
                    disabled={skipping}
                  >
                    {skipping ? 'Removing...' : 'Undo Skip'}
                  </Button>
                </Alert>
              )}
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Day of Week</strong></Form.Label>
                    <Form.Select 
                      value={selectedDay || ''} 
                      onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                      disabled={!selectedDay}
                    >
                      {daysOfWeek.map(day => (
                        <option key={day.id} value={day.id}>{day.day_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Time Slot</strong></Form.Label>
                    <Form.Select 
                      value={selectedSlot || ''} 
                      onChange={(e) => setSelectedSlot(parseInt(e.target.value))}
                      disabled={!selectedSlot}
                    >
                      {timeSlots.map(slot => (
                        <option key={slot.id} value={slot.id}>
                          {slot.slot_name} (Cutoff: {formatCutoffTime(slot.cutoff_time)})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {selectedDay && selectedSlot && (() => {
                const selectedSlotData = timeSlots.find(s => s.id === selectedSlot);
                const selectedDayData = daysOfWeek.find(d => d.id === selectedDay);
                
                if (!selectedSlotData || !selectedDayData) return null;

                const currentTime = getCurrentTimeMinutes();
                const currentJSDay = getCurrentDayOfWeek();
                const currentDBDay = currentJSDay === 0 ? 7 : currentJSDay;
                
                const cutoffMinutes = parseCutoffTime(selectedSlotData.cutoff_time);
                const isToday = currentDBDay === selectedDay;
                const minutesUntilCutoff = cutoffMinutes - currentTime;
                const isClosed = isToday && minutesUntilCutoff <= 0;
                const isClosingSoon = isToday && minutesUntilCutoff > 0 && minutesUntilCutoff <= 60;

                return (
                  <Alert variant={isClosed ? 'danger' : isClosingSoon ? 'warning' : 'info'} className="mb-4">
                    {isClosed ? (
                      <>
                        <strong>⚠️ This time slot has closed for today.</strong>
                        <br />
                        Cutoff was at {formatCutoffTime(selectedSlotData.cutoff_time)}. Please select a different day or time slot.
                      </>
                    ) : isClosingSoon ? (
                      <>
                        <strong>⏰ Closing Soon!</strong>
                        <br />
                        Only {minutesUntilCutoff} minutes left to place your order. Cutoff is at {formatCutoffTime(selectedSlotData.cutoff_time)}.
                        {existingSubmittedOrder && <><br /><span className="text-info">You can still revise your submitted order.</span></>}
                      </>
                    ) : (
                      <>
                        <strong>📅 {selectedDayData.day_name} - {selectedSlotData.slot_name}</strong>
                        <br />
                        Cutoff time: {formatCutoffTime(selectedSlotData.cutoff_time)}
                        {isToday && ` (${minutesUntilCutoff} minutes remaining)`}
                        {existingSubmittedOrder && <><br /><span className="text-info">You can revise your submitted order until the cutoff.</span></>}
                      </>
                    )}
                  </Alert>
                );
              })()}

              <Card className="mb-4">
                <Card.Header className="bg-light">
                  <strong>Order Items</strong>
                  {isOrderFormReadOnly && <span className="ms-2 text-muted">(Read-only - Cutoff Passed)</span>}
                  {!isOrderFormReadOnly && existingSubmittedOrder && <span className="ms-2 text-info">(Revising submitted order)</span>}
                </Card.Header>
                <Card.Body>
                  <Row>
                    {MENU_CATEGORIES.map(category => (
                      <Col md={4} key={category}>
                        <h6 className="mb-3 text-primary">{category}</h6>
                        {menuItems[category]?.map(item => {
                          const isLocked = item.locked_for_orders;
                          const quantity = currentOrder[item.id] || 0;
                          const standingQty = standingOrderQuantities[item.id] || 0;
                          const isModified = quantity !== standingQty;
                          
                          // For locked items: can adjust but not exceed standing order quantity
                          const canIncrease = !isLocked || quantity < standingQty;
                          const canDecrease = quantity > 0;
                          
                          const handleLockedQuantityChange = (newQty) => {
                            if (isLocked) {
                              // Can adjust between 0 and standing order quantity
                              updateQuantity(item.id, Math.max(0, Math.min(newQty, standingQty)));
                            } else {
                              updateQuantity(item.id, newQty);
                            }
                          };
                          
                          return (
                            <div key={item.id} 
                                 className="d-flex align-items-center mb-2"
                                 style={isModified ? { 
                                   borderLeft: '3px solid #f57c00', 
                                   paddingLeft: '8px',
                                   backgroundColor: '#fff8e1',
                                   borderRadius: '4px',
                                   padding: '4px 4px 4px 8px'
                                 } : {}}>
                              <div className="flex-grow-1">
                                <small>
                                  {isLocked && <span title="Locked - can adjust between 0 and standing order">🔒 </span>}
                                  {item.item_name}
                                </small>
                                {isModified && (
                                  <div style={{ fontSize: '0.7rem' }} className="text-warning">
                                    Standing: {standingQty}
                                  </div>
                                )}
                              </div>
                              <div className="quantity-control ms-2">
                                <button 
                                  className="quantity-btn"
                                  onClick={() => handleLockedQuantityChange(quantity - 1)}
                                  disabled={isOrderFormReadOnly || !canDecrease}
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  className="quantity-input"
                                  value={quantity}
                                  onChange={(e) => handleLockedQuantityChange(parseInt(e.target.value) || 0)}
                                  min="0"
                                  max={isLocked ? standingQty : undefined}
                                  disabled={isOrderFormReadOnly}
                                  style={isModified ? { backgroundColor: '#fff3e0' } : {}}
                                />
                                <button 
                                  className="quantity-btn"
                                  onClick={() => handleLockedQuantityChange(quantity + 1)}
                                  disabled={isOrderFormReadOnly || !canIncrease}
                                  title={isLocked && quantity >= standingQty ? "Cannot exceed standing order quantity" : ""}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>

              <Form.Group className="mb-3">
                <Form.Label><strong>Order Notes (Optional)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions..."
                  disabled={isOrderFormReadOnly}
                />
              </Form.Group>

              {orderMessage.text && orderMessage.type !== 'success' && (
                <Alert 
                  variant={orderMessage.type} 
                  onClose={() => setOrderMessage({ type: '', text: '' })} 
                  dismissible
                  className="mb-3"
                >
                  {orderMessage.text}
                </Alert>
              )}

              {isOrderFormReadOnly ? (
                <div className="d-flex gap-3 align-items-center">
                  <Alert variant="secondary" className="mb-0 flex-grow-1">
                    <strong>📋 Order submitted on {new Date(existingSubmittedOrder.submitted_at).toLocaleString()}</strong>
                    <br />
                    <small>Cutoff has passed. To make changes, please contact the store directly.</small>
                  </Alert>
                </div>
              ) : currentSkip ? (
                <div className="text-center">
                  <Alert variant="secondary" className="mb-0">
                    <strong>⏭️ This order is skipped.</strong> No order will be submitted for this date.
                    <br />
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="mt-2"
                      onClick={handleUnskipOrder}
                      disabled={skipping}
                    >
                      {skipping ? 'Removing...' : 'Undo Skip'}
                    </Button>
                  </Alert>
                </div>
              ) : (
                <div className="text-center">
                  <div className="d-flex justify-content-center gap-3">
                    <Button 
                      variant={existingSubmittedOrder ? "warning" : "primary"}
                      size="lg" 
                      onClick={() => handleSubmitOrder(false)}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting...
                        </>
                      ) : existingSubmittedOrder ? (
                        '🔄 Revise Order'
                      ) : (
                        '🍕 Submit Order'
                      )}
                    </Button>
                    {hasStandingOrderForSlot() && (
                      <Button
                        variant="outline-secondary"
                        size="lg"
                        onClick={handleSkipOrder}
                        disabled={skipping}
                        title="Skip this order without changing your standing order"
                      >
                        {skipping ? 'Skipping...' : '⏭️ Skip this order'}
                      </Button>
                    )}
                  </div>
                  {existingSubmittedOrder && (
                    <div className="text-muted mt-2">
                      <small>This will replace your previously submitted order.</small>
                    </div>
                  )}
                  {hasStandingOrderForSlot() && (
                    <div className="text-muted mt-2">
                      <small>Skip cancels only this order. Your standing order stays the same for future weeks.</small>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="standing" title="Standing Orders">
          <Card className="fade-in">
            <Card.Header>🔄 Standing Orders - Recurring Weekly Orders</Card.Header>
            <Card.Body>
              <Alert variant="success" className="mb-3">
                <strong>Recurring Orders:</strong> Set your default quantities for each day and time slot. 
                These will be used automatically when you place orders, and you can adjust individual orders in the "Adjust Your Next Order" tab.
              </Alert>

              <Alert variant="warning" className="mb-4">
                <strong>⏰ Important: 48-Hour Advance Notice Required</strong>
                <div className="mt-2">
                  Changes to standing orders take effect <strong>48 hours after submission</strong> to allow adequate prep time for specialty items.
                </div>
                <div className="mt-2">
                  <strong>Need urgent changes?</strong> Please contact the store directly.
                </div>
              </Alert>

              <div className="mb-3">
                <h5>Standing Orders Grid</h5>
                <p className="text-muted">Set default quantities for each day and time slot</p>
              </div>

              {daysOfWeek.map(day => (
                <div key={day.id} className="mb-4">
                  <h6 className="text-primary">{day.day_name}</h6>
                  
                  {timeSlots.filter(slot => slot.active).map(slot => {
                    // Check if this day/slot has any items with quantity > 0
                    const hasItems = MENU_CATEGORIES.some(cat =>
                      menuItems[cat]?.some(item => (editingStandingOrders[`${day.id}-${slot.id}-${item.id}`] || 0) > 0)
                    );
                    
                    const handleClearSlot = () => {
                      const updates = {};
                      MENU_CATEGORIES.flatMap(cat => menuItems[cat] || []).forEach(item => {
                        updates[`${day.id}-${slot.id}-${item.id}`] = 0;
                      });
                      setEditingStandingOrders(prev => ({ ...prev, ...updates }));
                    };
                    
                    return (
                    <Card key={slot.id} className="mb-3">
                      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{slot.slot_name}</strong>
                          <small className="text-muted ms-2">(Cutoff: {formatCutoffTime(slot.cutoff_time)})</small>
                        </div>
                        {hasItems && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={handleClearSlot}
                            title="Clear all items for this time slot"
                          >
                            🗑️ Clear All
                          </Button>
                        )}
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          {MENU_CATEGORIES.map(category => (
                            <Col md={4} key={category}>
                              <h6 className="mb-3">{category}</h6>
                              {menuItems[category]?.map(item => {
                                const key = `${day.id}-${slot.id}-${item.id}`;
                                const quantity = editingStandingOrders[key] || 0;
                                
                                return (
                                  <div key={item.id} className="d-flex align-items-center mb-2">
                                    <div className="flex-grow-1">
                                      <small>{item.item_name}</small>
                                    </div>
                                    <div className="quantity-control ms-2">
                                      <button 
                                        className="quantity-btn"
                                        onClick={() => updateStandingOrderQuantity(day.id, slot.id, item.id, quantity - 1)}
                                      >
                                        −
                                      </button>
                                      <input
                                        type="number"
                                        className="quantity-input"
                                        value={quantity}
                                        onChange={(e) => updateStandingOrderQuantity(day.id, slot.id, item.id, parseInt(e.target.value) || 0)}
                                        min="0"
                                      />
                                      <button 
                                        className="quantity-btn"
                                        onClick={() => updateStandingOrderQuantity(day.id, slot.id, item.id, quantity + 1)}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                    );
                  })}
                </div>
              ))}

              <div className="text-center mt-4">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={handleSaveAllStandingOrders}
                  disabled={savingStandingOrders}
                >
                  {savingStandingOrders ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    '💾 Save All Standing Orders'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>

          {orderSkips.length > 0 && (
            <Card className="mt-3 fade-in">
              <Card.Header>⏭️ Upcoming Skips</Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  These dates will be skipped. No order will be auto-submitted. Your standing order is unchanged for other weeks.
                </p>
                {orderSkips.map(skip => (
                  <div key={skip.id} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                    <div>
                      <strong>{skip.day_name}</strong> {skip.slot_name}
                      <span className="text-muted ms-2">
                        ({new Date(skip.skip_date + 'T12:00:00').toLocaleDateString()})
                      </span>
                    </div>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={async () => {
                        try {
                          await orderSkipsAPI.remove(skip.id);
                          const skipsRes = await orderSkipsAPI.get();
                          setOrderSkips(skipsRes.data);
                          setMessage({ type: 'success', text: `Skip removed for ${skip.day_name} ${skip.slot_name}` });
                        } catch (error) {
                          setMessage({ type: 'danger', text: 'Failed to remove skip' });
                        }
                      }}
                    >
                      Remove Skip
                    </Button>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}
        </Tab>

        <Tab eventKey="history" title="Order History">
          <Card className="fade-in">
            <Card.Header>📜 Order History</Card.Header>
            <Card.Body>
              {orderHistory.length === 0 ? (
                <p className="text-muted">No orders yet</p>
              ) : (
                orderHistory.map(order => {
                  // Group items by category
                  const groupedItems = Object.fromEntries(MENU_CATEGORIES.map(c => [c, []]));
                  order.items.forEach(item => {
                    const cat = item.category || 'MISC';
                    if (groupedItems[cat]) {
                      groupedItems[cat].push(item);
                    } else {
                      groupedItems.MISC.push(item);
                    }
                  });

                  // Order type badge styling
                  const orderTypeStyles = {
                    standing: { label: '🔄 Standing Order', bg: 'success' },
                    manual: { label: '✏️ Manual Order', bg: 'primary' },
                    modified: { label: '⚡ Modified Order', bg: 'warning' }
                  };
                  const typeStyle = orderTypeStyles[order.order_type] || orderTypeStyles.manual;
                  
                  return (
                    <Card key={order.id} className="mb-3" 
                          style={order.order_type === 'modified' ? { borderLeft: '4px solid #f57c00' } : {}}>
                      <Card.Body>
                        <Row>
                          <Col md={8}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <h6 className="mb-0">{order.day_name} - {order.slot_name}</h6>
                              <span className={`badge bg-${typeStyle.bg}`} style={{ fontSize: '0.7rem' }}>
                                {typeStyle.label}
                              </span>
                            </div>
                            <p className="text-muted mb-2">
                              <small>Order Date: {order.order_date.split('T')[0]}</small><br/>
                              <small>Submitted: {new Date(order.submitted_at).toLocaleString()}</small>
                            </p>
                            <small>
                              {MENU_CATEGORIES.map(cat => groupedItems[cat].length > 0 && (
                                <div key={cat}><strong style={{color: CATEGORY_STYLES[cat].color}}>{CATEGORY_STYLES[cat].emoji} {CATEGORY_STYLES[cat].label}:</strong> {groupedItems[cat].map(i => `${i.quantity}x ${i.item_name}`).join(', ')}</div>
                              ))}
                            </small>
                            {order.notes && <p><em>Notes: {order.notes}</em></p>}
                          </Col>
                          <Col md={4} className="text-end">
                            <span className={`badge fs-6 ${
                              order.status === 'completed' ? 'bg-success' : 
                              order.status === 'submitted' ? 'bg-primary' :
                              order.status === 'replaced' ? 'bg-secondary' : 
                              order.status === 'cancelled' ? 'bg-danger' :
                              'bg-warning'
                            }`}>
                              {order.status}
                            </span>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Duplicate Order Confirmation Modal */}
      <Modal show={showDuplicateConfirm} onHide={() => setShowDuplicateConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Replace Existing Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You already have an order submitted for this day and time slot.</p>
          <p><strong>Do you want to replace it with this new order?</strong></p>
          <p className="text-muted small">
            The previous order will be marked as "replaced" and only your new order will be processed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDuplicateConfirm(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleConfirmDuplicateOrder} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Yes, Replace Order'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Order Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>✅ Order Submitted!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🍕</div>
          <h4>Your order has been submitted successfully!</h4>
          <p className="mb-1"><strong>{successDetails.dayName} - {successDetails.slotName}</strong></p>
          <p className="text-muted">{successDetails.orderDate}</p>
          <p className="mt-3 small text-muted">
            You will receive a confirmation email shortly.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="success" size="lg" onClick={() => setShowSuccessModal(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Standing Order Success Modal */}
      <Modal show={showStandingSuccessModal} onHide={() => setShowStandingSuccessModal(false)} centered>
        <Modal.Header closeButton className={standingSuccessDetails.daysUntilEffective > 0 ? "bg-info text-white" : "bg-success text-white"}>
          <Modal.Title>✅ Standing Order Updated!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔄</div>
          <h4>Your standing order has been updated!</h4>
          <p className="mb-1"><strong>{standingSuccessDetails.dayName} - {standingSuccessDetails.slotName}</strong></p>
          
          {standingSuccessDetails.daysUntilEffective > 0 ? (
            <>
              <div className="alert alert-info mt-3 mb-0">
                <strong>⏰ Takes effect on:</strong>
                <br />
                <span style={{ fontSize: '1.1rem' }}>{standingSuccessDetails.effectiveDate}</span>
                <br />
                <small className="text-muted">({standingSuccessDetails.daysUntilEffective} days from now)</small>
              </div>
              <p className="mt-3 small text-muted">
                Need changes sooner? Please call the store.
              </p>
            </>
          ) : (
            <div className="alert alert-success mt-3 mb-0">
              <strong>⚡ Effective immediately!</strong>
              <br />
              <small>Your changes are now active.</small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button 
            variant={standingSuccessDetails.daysUntilEffective > 0 ? "info" : "success"} 
            size="lg" 
            onClick={() => setShowStandingSuccessModal(false)}
          >
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CustomerPortal;
